import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import { 
  getQuoteRequestDetails, 
  acceptQuote, 
  declineQuote, 
  getQuoteMessages, 
  sendQuoteMessage, 
  sendRevisedQuote, 
  getQuoteVersions, 
  closeNegotiation 
} from '../../api/quotes';
import { uploadOfferingImage } from '../../api/offerings';
import AdventureDateTimePicker from '../../Components/Shared/AdventureDateTimePicker';
import '../AdventureShared.css';
import './QuoteDetailPage.css';

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  // Core details state
  const [quote, setQuote] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Negotiation thread & version history states
  const [messages, setMessages] = useState([]);
  const [versions, setVersions] = useState([]);
  const [newMessageBody, setNewMessageBody] = useState('');
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedVersionId, setExpandedVersionId] = useState(null);

  // Modals visibility states
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [editQuoteModalOpen, setEditQuoteModalOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Forms inputs states
  const [declineReason, setDeclineReason] = useState('price_too_high');
  const [declineMessage, setDeclineMessage] = useState('');
  const [closeReason, setCloseReason] = useState('');

  // Revised quote inputs states
  const [quotedPrice, setQuotedPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [scope, setScope] = useState('');
  const [proposedStartDate, setProposedStartDate] = useState('');
  const [proposedEndDate, setProposedEndDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [providerNotes, setProviderNotes] = useState('');

  const currentUserId = localStorage.getItem("userId") || "";

  // Scroll chat window to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchDetails = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      // 1. Get quote request overview
      const data = await getQuoteRequestDetails(id);
      setQuote(data);

      // 2. Fetch negotiation messages
      const msgsData = await getQuoteMessages(id);
      setMessages(msgsData || []);

      // 3. Fetch quote versions
      const versionsData = await getQuoteVersions(id);
      setVersions(versionsData || []);
    } catch (err) {
      console.error("Failed to load quote details:", err);
      setError("Failed to load quote details.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [id, fetchDetails]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const Shell = ({ children }) => (
    <div className="QuoteDetailPage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>
        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>
          <main className="adv-page-main">{children}</main>
        </div>
      </div>
    </div>
  );

  if (isLoading) return <Shell><div className="adv-state-text" style={{ paddingTop: '80px' }}>Loading quote request thread...</div></Shell>;

  if (error || !quote) return (
    <Shell>
      <div className="adv-state-error" style={{ paddingTop: '80px', textAlign: 'center' }}>
        <h3>{error || "Quote request not found!"}</h3>
        <button className="adv-btn-primary" style={{ marginTop: '16px' }} onClick={() => navigate('/facilitate/quotes')}>
          Back to Quote Requests
        </button>
      </div>
    </Shell>
  );

  const isCustomer = (quote.customerId === currentUserId || quote.customer?.userId === currentUserId);
  const isProvider = (quote.providerId === currentUserId || quote.provider?.userId === currentUserId);

  const formatPriceVal = (amount, curr) => {
    if (amount === undefined || amount === null) return 'N/A';
    let cleanCurrency = (curr || 'USD').trim().toUpperCase();
    if (cleanCurrency === 'RS') cleanCurrency = 'INR';
    if (!/^[A-Z]{3}$/.test(cleanCurrency)) cleanCurrency = 'USD';

    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cleanCurrency }).format(amount);
    } catch (e) {
      return `${cleanCurrency} ${(amount || 0).toFixed(2)}`;
    }
  };

  const statusColor = (s) => {
    const map = { 
      requested: '#f5c842', 
      pending: '#f5c842', 
      quoted: '#58a6ff', 
      accepted: '#28a745', 
      declined: '#dc3545', 
      closed: '#8b949e',
      expired: '#8b949e' 
    };
    return map[s] || '#8b949e';
  };

  // ────────────────────────────────────────────────────────
  // ACTIONS
  // ────────────────────────────────────────────────────────

  // Customer accepts the active quote
  const handleAcceptAction = () => {
    setAcceptModalOpen(true);
  };

  const handleAcceptSubmit = async (e) => {
    e.preventDefault();
    setIsAccepting(true);
    try {
      const res = await acceptQuote(quote.id);
      setAcceptModalOpen(false);
      if (res?.bookingId) {
        navigate(`/facilitate/orders/${res.bookingId}`);
      } else {
        fetchDetails();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to accept quote.");
      setIsAccepting(false);
    }
  };

  // Decline quote
  const handleDeclineSubmit = async (e) => {
    e.preventDefault();
    setIsDeclining(true);
    try {
      await declineQuote(quote.id, declineReason, declineMessage);
      setDeclineModalOpen(false);
      fetchDetails();
    } catch (err) {
      console.error(err);
      alert("Failed to decline quote.");
    } finally {
      setIsDeclining(false);
    }
  };

  // Open the revised quote modal
  const handleOpenEditModal = () => {
    setQuotedPrice(quote.quotedPrice || quote.budgetHint || '');
    setCurrency(quote.currency || 'USD');
    setScope(quote.scope || quote.description || '');
    setProposedStartDate(quote.proposedStartDate ? quote.proposedStartDate.split('T')[0] : (quote.preferredStartDate ? quote.preferredStartDate.split('T')[0] : ''));
    setProposedEndDate(quote.proposedEndDate ? quote.proposedEndDate.split('T')[0] : (quote.preferredEndDate ? quote.preferredEndDate.split('T')[0] : ''));
    setValidUntil(quote.validUntil ? quote.validUntil.split('T')[0] : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setProviderNotes(quote.notes || '');
    setEditQuoteModalOpen(true);
  };

  // Provider sends a revised quote
  const handleEditQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingEdit(true);
    try {
      await sendRevisedQuote(quote.id, {
        quotedPrice: parseFloat(quotedPrice),
        currency,
        scope,
        proposedStartDate: proposedStartDate || null,
        proposedEndDate: proposedEndDate || null,
        validUntil,
        notes: providerNotes
      });
      setEditQuoteModalOpen(false);
      fetchDetails();
    } catch (err) {
      console.error(err);
      alert("Failed to send revised quote.");
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Provider closes negotiation
  const handleCloseNegotiationSubmit = async (e) => {
    e.preventDefault();
    setIsClosing(true);
    try {
      await closeNegotiation(quote.id, { reason: closeReason });
      setCloseModalOpen(false);
      fetchDetails();
    } catch (err) {
      console.error(err);
      alert("Failed to close negotiation.");
    } finally {
      setIsClosing(false);
    }
  };

  // Chat message attachments uploading (max 3 per message)
  const handleMessageFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (messageAttachments.length + files.length > 3) {
      alert("You can attach up to 3 files per message.");
      return;
    }
    setIsUploading(true);
    for (const file of files) {
      try {
        const res = await uploadOfferingImage(file);
        if (res?.url) {
          setMessageAttachments(prev => [...prev, res.url]);
        }
      } catch (err) {
        console.error("Message upload failed for file: " + file.name, err);
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }
    setIsUploading(false);
  };

  const removeAttachment = (idx) => {
    setMessageAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  // Send a regular text/attachment message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessageBody.trim() && messageAttachments.length === 0) return;
    setIsSendingMessage(true);
    try {
      await sendQuoteMessage(quote.id, {
        body: newMessageBody,
        attachmentUrls: messageAttachments
      });
      setNewMessageBody('');
      setMessageAttachments([]);
      fetchDetails();
    } catch (err) {
      console.error(err);
      alert("Failed to send message.");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const isThreadLocked = quote.status === 'accepted' || quote.status === 'closed' || quote.status === 'declined' || quote.status === 'expired';

  return (
    <div className="QuoteDetailPage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>

        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>

          <main className="adv-page-main">
            {/* Page Header */}
            <div className="adv-detail-page-header">
              <div>
                <h1 className="adv-page-title">Quote <span className="adv-page-title-accent">Negotiation</span></h1>
                <p className="adv-page-sub">Custom Quote Request # {quote.id}</p>
              </div>
              <button className="adv-btn-outline" onClick={() => isProvider ? navigate('/facilitate/quotes/inbox') : navigate('/facilitate/quotes')}>
                ← Back
              </button>
            </div>

            {/* Split layout grid */}
            <div className="adv-negotiation-grid">
              
              {/* ────────────────────────────────────────────────────────
                  LEFT: Chat Thread
                  ──────────────────────────────────────────────────────── */}
              <div className="adv-chat-thread">
                <div className="adv-chat-messages-container">
                  {messages.map((msg) => {
                    const isMsgSelf = msg.senderId === currentUserId;
                    const isSystem = msg.type === 'system' || msg.senderRole === 'system';
                    const isQuote = msg.type === 'quote';

                    if (isSystem) {
                      return (
                        <div key={msg.id} className="adv-chat-row adv-chat-row--system">
                          <span className="adv-chat-bubble adv-chat-bubble--system">
                            ⚙️ {msg.body}
                            <span className="adv-chat-meta" style={{ display: 'block', marginTop: '2px', fontSize: '10px' }}>
                              {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div key={msg.id} className={`adv-chat-row ${isMsgSelf ? 'adv-chat-row--customer' : 'adv-chat-row--provider'}`}>
                        <div className={`adv-chat-bubble ${isMsgSelf ? 'adv-chat-bubble--customer' : 'adv-chat-bubble--provider'}`}>
                          
                          {/* Sender name label */}
                          <div style={{ fontWeight: '700', fontSize: '12px', color: '#48d1cc', marginBottom: '4px' }}>
                            {isMsgSelf ? "You" : (msg.senderRole === 'provider' ? quote.providerName : quote.customerName)}
                          </div>

                          {/* Message Text */}
                          <div style={{ whiteSpace: 'pre-line' }}>{msg.body}</div>

                          {/* Attachment uploads */}
                          {msg.attachmentUrls?.length > 0 && (
                            <div className="adv-chat-message-attachments">
                              {msg.attachmentUrls.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                  <img src={url} alt="Attachment Preview" className="adv-chat-message-attachment-thumb" />
                                </a>
                              ))}
                            </div>
                          )}

                          {/* Embedded Quote Card details */}
                          {isQuote && msg.quoteVersion && (
                            <div className="adv-thread-quote-card">
                              <div className="adv-thread-quote-card__header">
                                <span className="adv-thread-quote-card__price">
                                  {formatPriceVal(msg.quoteVersion.quotedPrice, msg.quoteVersion.currency)}
                                </span>
                                <span className="adv-thread-quote-card__version">
                                  Rev #{msg.quoteVersion.versionNumber}
                                </span>
                              </div>
                              <div className="adv-thread-quote-card__detail">
                                <strong>Scope:</strong> {msg.quoteVersion.scope}
                              </div>
                              {msg.quoteVersion.notes && (
                                <div className="adv-thread-quote-card__detail" style={{ fontStyle: 'italic', fontSize: '12px' }}>
                                  <strong>Changes Notes:</strong> {msg.quoteVersion.notes}
                                </div>
                              )}
                              <div className="adv-thread-quote-card__detail" style={{ fontSize: '11px', color: '#a1a1aa', marginTop: '6px' }}>
                                📅 Timeline: {msg.quoteVersion.proposedStartDate ? new Date(msg.quoteVersion.proposedStartDate).toLocaleDateString() : 'Flexible'} 
                                {msg.quoteVersion.proposedEndDate ? ` to ${new Date(msg.quoteVersion.proposedEndDate).toLocaleDateString()}` : ''}
                                <br />
                                ⏳ Valid until: {new Date(msg.quoteVersion.validUntil).toLocaleDateString()}
                              </div>
                              
                              {/* Inline customer actions on quote cards */}
                              {isCustomer && msg.quoteVersion.status === 'pending' && quote.status === 'quoted' && (
                                <div className="adv-thread-quote-card__actions">
                                  <button className="adv-btn-success" style={{ flexGrow: 1, padding: '6px' }} onClick={handleAcceptAction}>
                                    Accept Quote
                                  </button>
                                  <button className="adv-btn-danger-outline" style={{ flexGrow: 1, padding: '6px' }} onClick={() => setDeclineModalOpen(true)}>
                                    Decline
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Time details */}
                          <div className={`adv-chat-meta ${isMsgSelf ? 'adv-chat-meta--customer' : 'adv-chat-meta--provider'}`}>
                            {new Date(msg.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>

                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Composer block */}
                {isThreadLocked ? (
                  <div className="adv-chat-composer adv-chat-composer--locked">
                    🔒 Negotiation Closed (Status: {quote.status.toUpperCase()})
                  </div>
                ) : (
                  <div className="adv-chat-composer">
                    {/* Attachments preview row */}
                    {messageAttachments.length > 0 && (
                      <div className="adv-chat-attachments-list">
                        {messageAttachments.map((url, i) => (
                          <div key={i} className="adv-chat-attachment-chip">
                            📎 File #{i+1}
                            <button type="button" className="adv-chat-attachment-chip-remove" onClick={() => removeAttachment(i)}>&times;</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Textarea row */}
                    <form onSubmit={handleSendMessage} className="adv-chat-composer-row">
                      <textarea
                        className="adv-chat-composer-textarea"
                        placeholder="Write a message to discuss project details..."
                        value={newMessageBody}
                        onChange={(e) => setNewMessageBody(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                      />
                      <button 
                        type="submit" 
                        className="adv-chat-send-btn" 
                        disabled={isSendingMessage || (!newMessageBody.trim() && messageAttachments.length === 0)}
                      >
                        {isSendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </form>

                    {/* Controls row */}
                    <div className="adv-chat-controls">
                      <div className="adv-chat-controls-left">
                        <label className="adv-btn-outline" style={{ cursor: 'pointer', padding: '6px 12px', fontSize: '12px' }}>
                          {isUploading ? "📎 Uploading..." : "📎 Attach File"}
                          <input 
                            type="file" 
                            multiple 
                            style={{ display: 'none' }} 
                            onChange={handleMessageFileUpload}
                            disabled={isUploading || messageAttachments.length >= 3} 
                          />
                        </label>
                      </div>

                      {/* Providers option actions */}
                      {isProvider && (
                        <div className="adv-chat-controls-right">
                          <button className="adv-btn-primary" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={handleOpenEditModal}>
                            Revised Quote
                          </button>
                          <button className="adv-btn-danger-outline" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => setCloseModalOpen(true)}>
                            Close Negotiation
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ────────────────────────────────────────────────────────
                  RIGHT: Proposal Details & Versions Panel
                  ──────────────────────────────────────────────────────── */}
              <div className="adv-detail-right">
                
                {/* Active Proposal overview */}
                <div className="adv-card">
                  <div className="adv-detail-meta-row" style={{ marginTop: 0, justifyContent: 'space-between' }}>
                    <span className="adv-status" style={{ color: statusColor(quote.status), backgroundColor: `${statusColor(quote.status)}18` }}>
                      {quote.status === 'requested' ? 'Awaiting Quote' : quote.status}
                    </span>
                    <span className="adv-detail-date-note">{new Date(quote.createdAt).toLocaleDateString()}</span>
                  </div>

                  <h3 className="adv-card-title" style={{ marginTop: '16px' }}>Project Specification</h3>
                  <p className="adv-note-text" style={{ fontSize: '14px', lineHeight: 1.5, color: '#e6edf3' }}>
                    {quote.description}
                  </p>

                  <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px' }}>
                    <div className="adv-schedule-row">
                      <span className="adv-detail-label">Current Quote Price:</span>
                      <strong className="adv-schedule-price" style={{ color: quote.quotedPrice ? '#28a745' : '#f5c842', fontSize: '20px' }}>
                        {quote.quotedPrice ? formatPriceVal(quote.quotedPrice, quote.currency) : "Awaiting Quote"}
                      </strong>
                    </div>

                    {quote.scope && (
                      <div style={{ marginTop: '12px' }}>
                        <span className="adv-detail-label">Proposed Scope:</span>
                        <p className="adv-note-text" style={{ fontSize: '13px', color: '#cbd5e1' }}>{quote.scope}</p>
                      </div>
                    )}

                    {(quote.proposedStartDate || quote.proposedEndDate) && (
                      <div style={{ marginTop: '8px' }}>
                        <span className="adv-detail-label">Timeline:</span>
                        <p className="adv-note-text" style={{ fontSize: '13px', color: '#cbd5e1' }}>
                          {quote.proposedStartDate ? new Date(quote.proposedStartDate).toLocaleDateString() : ''} 
                          {quote.proposedEndDate ? ` to ${new Date(quote.proposedEndDate).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Actions for customer */}
                  {isCustomer && quote.status === 'quoted' && (
                    <div className="adv-action-btns" style={{ marginTop: '20px' }}>
                      <button className="adv-btn-success adv-btn--full" onClick={handleAcceptAction}>
                        ✓ Accept Proposal
                      </button>
                      <button className="adv-btn-danger-outline adv-btn--full" onClick={() => setDeclineModalOpen(true)}>
                        Decline
                      </button>
                    </div>
                  )}

                  {isCustomer && quote.status === 'requested' && (
                    <div style={{ marginTop: '16px', fontStyle: 'italic', fontSize: '12px', color: '#8b949e', textAlign: 'center' }}>
                      Waiting for provider to send the initial custom quote. You can discuss details in the chat.
                    </div>
                  )}

                  {quote.status === 'accepted' && quote.bookingId && (
                    <button className="adv-btn-primary adv-btn--full" style={{ marginTop: '20px' }} onClick={() => navigate(`/facilitate/orders/${quote.bookingId}`)}>
                      View Linked Order
                    </button>
                  )}
                </div>

                {/* Quote Version History Accordion */}
                <div className="adv-versions-panel">
                  <h4 style={{ margin: 0, fontWeight: '700', fontSize: '15px', color: '#fff' }}>Quote Revisions History ({versions.length})</h4>
                  
                  {versions.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#777', fontStyle: 'italic' }}>No quote iterations sent yet.</div>
                  ) : (
                    versions.map((ver) => {
                      const isExpanded = expandedVersionId === ver.id;
                      return (
                        <div key={ver.id} className={`adv-version-item adv-version-item--${ver.status}`}>
                          <div 
                            className="adv-version-item__header"
                            onClick={() => setExpandedVersionId(isExpanded ? null : ver.id)}
                          >
                            <div>
                              <span className="adv-version-item__title">Revision #{ver.versionNumber}</span>
                              <span className="adv-detail-date-note" style={{ display: 'block', fontSize: '10px' }}>
                                {new Date(ver.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span className="adv-version-item__price">{formatPriceVal(ver.quotedPrice, ver.currency)}</span>
                              <span className="adv-version-item__status" style={{ 
                                display: 'block', 
                                fontSize: '9px',
                                color: statusColor(ver.status) 
                              }}>
                                {ver.status}
                              </span>
                            </div>
                          </div>

                          {/* Expanded section */}
                          {isExpanded && (
                            <div className="adv-version-item__body">
                              <div><strong>Scope of Work:</strong></div>
                              <div style={{ whiteSpace: 'pre-line', marginBottom: '8px', color: '#e6edf3' }}>{ver.scope}</div>
                              
                              {ver.notes && (
                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Changes Notes:</strong>
                                  <div style={{ color: '#e6edf3' }}>{ver.notes}</div>
                                </div>
                              )}

                              <div className="adv-version-details-grid">
                                <div>📅 <strong>Timeline:</strong> {ver.proposedStartDate ? new Date(ver.proposedStartDate).toLocaleDateString() : 'Flexible'} {ver.proposedEndDate ? `to ${new Date(ver.proposedEndDate).toLocaleDateString()}` : ''}</div>
                                <div>⌛ <strong>Valid Until:</strong> {new Date(ver.validUntil).toLocaleDateString()}</div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

              </div>

            </div>
          </main>
        </div>
      </div>

      {/* Decline Proposal Modal */}
      {declineModalOpen && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Decline Proposal?</h2>
            <form onSubmit={handleDeclineSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Reason *</label>
                <select value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} className="adv-modal__select">
                  <option value="price_too_high">Price too high</option>
                  <option value="dates_dont_work">Dates don't work</option>
                  <option value="unavailable">Unavailable</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Message (optional)</label>
                <textarea rows="3" placeholder="Explanation..." value={declineMessage} onChange={(e) => setDeclineMessage(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-modal-actions">
                <button 
                  type="button" 
                  className="adv-btn-ghost" 
                  onClick={() => setDeclineModalOpen(false)}
                  disabled={isDeclining}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="adv-btn-destructive"
                  disabled={isDeclining}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isDeclining ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Declining...
                    </>
                  ) : (
                    "Confirm Decline"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Revised Quote Modal (Provider Only) */}
      {editQuoteModalOpen && (
        <div className="adv-modal-overlay">
          <div className="adv-modal" style={{ maxWidth: '650px' }}>
            <h2 className="adv-modal__title">Send Revised Quote</h2>
            <form onSubmit={handleEditQuoteSubmit}>
              <div className="adv-form-grid-2">
                <div className="adv-modal__field">
                  <label className="adv-modal__label">Revised Price *</label>
                  <input type="number" required min="0" step="0.01" value={quotedPrice} onChange={(e) => setQuotedPrice(e.target.value)} className="adv-modal__input" />
                </div>
                <div className="adv-modal__field">
                  <label className="adv-modal__label">Currency *</label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="adv-modal__select">
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Scope of Work *</label>
                <textarea required rows="4" value={scope} onChange={(e) => setScope(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-form-grid-2">
                <div className="adv-modal__field">
                  <label className="adv-modal__label">Proposed Start Date</label>
                  <AdventureDateTimePicker
                    type="date"
                    label="Proposed Start Date"
                    value={proposedStartDate}
                    onChange={setProposedStartDate}
                    minDate={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="adv-modal__field">
                  <label className="adv-modal__label">Proposed End Date</label>
                  <AdventureDateTimePicker
                    type="date"
                    label="Proposed End Date"
                    value={proposedEndDate}
                    onChange={setProposedEndDate}
                    minDate={proposedStartDate || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Quote Valid Until *</label>
                <AdventureDateTimePicker
                  type="date"
                  label="Quote Valid Until *"
                  value={validUntil}
                  onChange={setValidUntil}
                  minDate={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">What changed? (Revision Notes) *</label>
                <textarea required rows="2" placeholder="Explain what revisions were made to price or scope..." value={providerNotes} onChange={(e) => setProviderNotes(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-modal-actions">
                <button 
                  type="button" 
                  className="adv-btn-ghost" 
                  onClick={() => setEditQuoteModalOpen(false)}
                  disabled={isSubmittingEdit}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="adv-btn-primary"
                  disabled={isSubmittingEdit}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isSubmittingEdit ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Sending...
                    </>
                  ) : (
                    "Send Revised Quote"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Close Negotiation Modal (Provider Only) */}
      {closeModalOpen && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Close Negotiation?</h2>
            <form onSubmit={handleCloseNegotiationSubmit}>
              <div style={{ fontSize: '13px', color: '#ff4d4f', marginBottom: '16px' }}>
                ⚠️ Closing the negotiation will lock this chat thread permanently. No further revisions or messages can be sent.
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Reason for closure (optional)</label>
                <textarea rows="3" placeholder="Explanation for closing..." value={closeReason} onChange={(e) => setCloseReason(e.target.value)} className="adv-modal__textarea" />
              </div>
              <div className="adv-modal-actions">
                <button 
                  type="button" 
                  className="adv-btn-ghost" 
                  onClick={() => setCloseModalOpen(false)}
                  disabled={isClosing}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="adv-btn-destructive"
                  disabled={isClosing}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isClosing ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Closing...
                    </>
                  ) : (
                    "Close Permanently"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Accept Quote Modal */}
      {acceptModalOpen && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">Accept Custom Quote?</h2>
            <form onSubmit={handleAcceptSubmit}>
              <div style={{ color: 'rgba(255, 255, 255, 0.72)', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
                You are about to accept the quote proposal from <strong>{quote.providerName}</strong> for 
                <strong style={{ color: '#28a745', marginLeft: '6px' }}>{formatPriceVal(quote.quotedPrice, quote.currency)}</strong>.
                <br /><br />
                This will automatically generate a confirmed booking contract. You will be redirected to the booking details tracker page.
              </div>
              <div className="adv-modal__actions">
                <button 
                  type="button" 
                  className="adv-btn-ghost" 
                  onClick={() => setAcceptModalOpen(false)}
                  disabled={isAccepting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="adv-btn-success" 
                  disabled={isAccepting}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isAccepting ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Accepting...
                    </>
                  ) : (
                    "✓ Yes, Accept Quote"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
